import { ref } from 'vue'
import { productApi, setApi, type CatalogEntry, type CatalogKind, type SetType } from '@/services/catalog'

/**
 * Carga de un registro del catálogo por identificador.
 *
 * El tipo lo dice la ruta: `/catalog/products/5` y `/catalog/sets/5`. Antes venía
 * de la query (`?kind=set`) porque la ruta era única, y eso obligaba a propagar el
 * tipo por todos los enlaces y a perderlo en cuanto una vista redirigía. Con el
 * tipo en la ruta no hay forma de que Show y Edit acaben llaman al endpoint
 * equivocado.
 *
 * `load()` sigue admitiendo no saber el tipo, y en ese caso prueba con productos y
 * cae a sets solo ante un 404. Cualquier otro error —red, 403— se propaga, porque
 * reintentar contra el otro endpoint lo convertiría en un fallo más difícil de
 * diagnosticar: un 403 en products podría ser de permisos, no de «no existe».
 */

export interface CatalogRecord extends CatalogEntry {
  kind: CatalogKind
  type: SetType | null
  created_at: string | null
  updated_at: string | null
}

function isNotFound(cause: unknown): boolean {
  const maybeAxios = cause as { response?: { status?: number } }
  return maybeAxios.response?.status === 404
}

function messageFor(cause: unknown, fallback: string): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? fallback
}

export function useCatalogRecord() {
  const record = ref<CatalogRecord | null>(null)
  const loading = ref(false)
  const errorMessage = ref<string | null>(null)

  /** Carga el registro. Sin `kindHint` se deduce probando y catching el 404. */
  async function load(id: number, kindHint?: CatalogKind | null): Promise<void> {
    loading.value = true
    errorMessage.value = null
    record.value = null

    try {
      if (kindHint === 'product') {
        const { data } = await productApi.get(id)
        record.value = { ...data.data, kind: 'product', type: null }
        return
      }

      if (kindHint === 'set') {
        const { data } = await setApi.get(id)
        record.value = { ...data.data, kind: 'set' }
        return
      }

      try {
        const { data } = await productApi.get(id)
        record.value = { ...data.data, kind: 'product', type: null }
      } catch (cause: unknown) {
        if (!isNotFound(cause)) throw cause
        const { data } = await setApi.get(id)
        record.value = { ...data.data, kind: 'set' }
      }
    } catch (cause: unknown) {
      record.value = null
      errorMessage.value = messageFor(
        cause,
        isNotFound(cause) ? 'Ese registro no existe en el catálogo.' : 'No se pudo cargar el registro.',
      )
    } finally {
      loading.value = false
    }
  }

  function reset(): void {
    record.value = null
    errorMessage.value = null
    loading.value = false
  }

  return { record, loading, errorMessage, load, reset }
}

/**
 * Traduce el `catalogKind` del `meta` de la ruta al discriminante del servicio.
 *
 * `products` → `product` y `sets` → `set`: en singular es lo que espera el backend.
 */
export function kindFromRoute(value: unknown): CatalogKind | null {
  if (value === 'products') return 'product'
  if (value === 'sets') return 'set'

  return null
}

/** Referencia a un id de ruta: siempre string, y `null` si falta o no es válido. */
export function idFromParam(value: unknown): number | null {
  const parsed = Number.parseInt(String(value ?? ''), 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}